import { useEffect } from "react";

/**
 * Radix modal primitives (Dialog, AlertDialog, Sheet, Popover) lock background
 * scrolling with `react-remove-scroll`, which registers a non-passive
 * `touchmove` listener on `document` and calls `preventDefault()` whenever the
 * gesture would not land on something scrollable in that direction.
 *
 * On iOS that also kills the native text-selection drag: moving a selection
 * handle sideways is cancelled (nothing scrolls horizontally) and moving it
 * down is cancelled while the dialog is scrolled to the top. Dragging up
 * happens to be allowed, after which Safari owns the gesture and the following
 * touchmoves are no longer cancelable - which is why a drag only works once it
 * has started upwards and is never released.
 *
 * We listen in the capture phase on `document`, so we run before the
 * bubble-phase listener inside `react-remove-scroll`, and drop the event
 * entirely while a selection inside a dialog is being dragged.
 */
const isIos = () =>
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS reports itself as a Mac, so fall back to touch support.
    (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

const hasSelectionInsideDialog = () => {
    const selection = document.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) return false;

    // The selection anchor is usually a text node, and during a handle drag the
    // touch target can be the overlay rather than the text, so key off where the
    // selection lives instead of where the finger currently is.
    const container = selection.getRangeAt(0).commonAncestorContainer;
    const element = container instanceof Element ? container : container.parentElement;

    return !!element?.closest("[role=dialog], [role=alertdialog]");
};

export function useAllowSelectionDrag() {
    useEffect(() => {
        if (!isIos()) return;

        const allowSelectionDrag = (event: TouchEvent) => {
            // Two fingers is a pinch/zoom, never a selection drag.
            if (event.touches.length > 1) return;
            if (!hasSelectionInsideDialog()) return;

            event.stopImmediatePropagation();
        };

        document.addEventListener("touchmove", allowSelectionDrag, { capture: true, passive: true });

        return () => {
            document.removeEventListener("touchmove", allowSelectionDrag, { capture: true });
        };
    }, []);
}
